import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// ─── Mapeamento etapa_atual (banco) → código curto (UI) ──
const ETAPA_MAP = {
  'EP01':                          'EP01',
  'EP02':                          'EP02',
  'ESTUDO PRELIMINAR':             'EP',
  'PROJETO E ESTUDO PRELIMINAR':   'EP',
  'EXECUTIVO E DETALHAMENTO':      'EXE',
  'EXECUÇÃO DA OBRA':              'OBR',
  'FINALIZADO':                    'FIN',
  'NÃO INICIADO':                  'NI',
}

// ─── Abreviações do tipo (tipo_codigo → label curto) ─────
const TIPO_LABEL = {
  'ARQ-R-NC':    'Arq. Res. NC',
  'ARQ-R-RF':    'Arq. Res. RF',
  'ARQ-C-NC':    'Arq. Com. NC',
  'ARQ-C-RF':    'Arq. Com. RF',
  'ARQ-R-NC_EP': 'EP Res. NC',
  'ARQ-R-RF_EP': 'EP Res. RF',
  'ARQ-C-NC_EP': 'EP Com. NC',
  'ARQ-C-RF_EP': 'EP Com. RF',
  'INT-R':       'Int. Res.',
  'INT-R_S':     'Int. Res. S.',
  'INT-C':       'Int. Com.',
  'INT-C_S':     'Int. Com. S.',
  'INT-TEC':     'Int. Técnico',
  'MAST':        'Masterplan',
  'PAI':         'Paisagismo',
  'BRAND':       'Branding',
  'RET-F-R':     'Retrofit Res.',
  'RET-F-C':     'Retrofit Com.',
}

// ─── Normalizar linha do banco → formato da UI ───────────
function normalizar(p) {
  const etapa  = ETAPA_MAP[p.etapa_atual] ?? (p.etapa_atual?.slice(0, 6) ?? '—')
  const status = p.status || ''

  // Múltiplos clientes via projeto_clientes; fallback para FK direto
  const pcNames = (p.projeto_clientes || [])
    .map(pc => pc.clientes?.nome_completo || pc.clientes?.nome_fantasia)
    .filter(Boolean)
  const directName = p.clientes?.nome_completo || p.clientes?.nome_fantasia
  const cliente = pcNames.length > 0 ? pcNames.join(', ') : (directName || '—')

  const tipoLabel = TIPO_LABEL[p.tipo_codigo] ?? p.tipo_codigo ?? '—'

  return {
    id:        p.id,
    codigo:    p.codigo_projeto || String(p.id),
    nome:      p.nome || '—',
    cliente,
    tipo:      p.tipo_codigo || '',
    tipoLabel,
    etapa,
    status,
    prazo:     null,   // campo futuro — revisoes.data_prevista
  }
}

// ─── Hook leve p/ dashboard (projetos ativos, sem joins) ──
export function useProjetosAtivos(limit = 20) {
  const [projetos, setProjetos] = useState([])
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const { data } = await supabase
        .from('projetos')
        .select('id, nome, etapa_atual, tipo_codigo, cor, criado_em, codigo_projeto, imagem_destaque_path')
        .eq('status', 'ATIVO')
        .order('criado_em', { ascending: false, nullsFirst: false })
        .limit(limit)
      if (!cancelled) { setProjetos(data ?? []); setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [limit])

  return { projetos, loading }
}

// ─── Hook leve p/ seletores (TODOS os projetos, sem joins) ─
// Lançamento financeiro pode ser retroativo: cobrança de projeto
// entregue ou pendente precisa aparecer no combobox igual aos ativos.
export function useProjetosParaSelecao() {
  const [projetos, setProjetos] = useState([])
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const { data } = await supabase
        .from('projetos')
        .select('id, nome, codigo_projeto, status')
        .order('codigo_projeto', { ascending: false, nullsFirst: false })
      if (!cancelled) { setProjetos(data ?? []); setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [])

  return { projetos, loading }
}

// ─── Hook principal ───────────────────────────────────────
export function useProjectos() {
  const [projetos,      setProjectos]      = useState([])
  const [tiposProjeto,  setTiposProjeto]   = useState([])
  const [loading,       setLoading]        = useState(true)
  const [error,         setError]          = useState(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const [projResult, tiposResult] = await Promise.all([
          supabase
            .from('projetos')
            .select(`
              id, codigo_projeto, nome, etapa_atual, status, tipo_codigo,
              tipos_projeto ( nome ),
              clientes!projetos_cliente_id_fkey ( nome_completo, nome_fantasia ),
              projeto_clientes ( papel, ativo, clientes ( nome_completo, nome_fantasia ) )
            `)
            .order('codigo_projeto', { ascending: false }),
          supabase
            .from('tipos_projeto')
            .select('codigo, nome')
            .eq('ativo', true)
            .order('nome'),
        ])

        if (cancelled) return
        if (projResult.error || tiposResult.error) {
          setError('Erro ao carregar projetos.')
          setLoading(false)
          return
        }
        setProjectos((projResult.data ?? []).map(normalizar))
        setTiposProjeto(tiposResult.data ?? [])
        setLoading(false)
      } catch {
        if (!cancelled) { setError('Erro ao carregar projetos.'); setLoading(false) }
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  async function updateProjeto(projectoId, fields) {
    // Optimistic update
    setProjectos(prev => prev.map(p => {
      if (p.id !== projectoId) return p
      const upd = { ...p }
      if (fields.tipo_codigo !== undefined) {
        upd.tipo      = fields.tipo_codigo || ''
        upd.tipoLabel = TIPO_LABEL[fields.tipo_codigo] ?? fields.tipo_codigo ?? '—'
      }
      if (fields.status !== undefined) upd.status = fields.status
      return upd
    }))
    const { error } = await supabase.from('projetos').update(fields).eq('id', projectoId)
    if (error) throw error
  }

  async function createProjeto({ nome, codigo_projeto, tipo_codigo, responsavel_id }: {
    nome: string; codigo_projeto: string; tipo_codigo: string; responsavel_id?: number
  }) {
    const cod = codigo_projeto.trim().toUpperCase()
    const { count } = await supabase
      .from('projetos')
      .select('*', { count: 'exact', head: true })
      .eq('codigo_projeto', cod)
    if (count && count > 0) throw new Error(`Código "${cod}" já está em uso`)

    const { data, error } = await supabase
      .from('projetos')
      .insert({ nome: nome.trim(), codigo_projeto: cod, tipo_codigo })
      .select('id, escritorio_id')
      .single()
    if (error) throw new Error(error.message)

    // Atribuir responsável ao projeto
    if (responsavel_id && data.escritorio_id) {
      await supabase.from('projeto_colaboradores').insert({
        projeto_id: data.id,
        colaborador_id: responsavel_id,
        papel: 'RESPONSAVEL',
        escritorio_id: data.escritorio_id,
      })
    }

    return data.id
  }

  return { projetos, tiposProjeto, loading, error, updateProjeto, createProjeto }
}
